// turf
import * as turf from '@turf/turf';

// mapbox
import mapboxgl from 'mapbox-gl';

// store
import { useStore } from '@/store';

export const useVertexHook = () => {
    // store
    const store = useStore();

    // generate draw layer label
    const generateDrawLayerLabel = (
        push: any,
        feature: any,
        Constants: any,
    ) => {
        if (feature.type === Constants.geojsonTypes.POLYGON) {
            feature.coordinates[0].forEach((element, index) => {
                let features = turf.points([
                    feature.coordinates[0][index],
                    feature.coordinates[0][index + 1]
                        ? feature.coordinates[0][index + 1]
                        : feature.coordinates[0][0],
                ]);

                let center = turf.center(features);
                push(
                    createVertex(
                        feature.id,
                        center.geometry?.coordinates,
                        '0.0',
                        false,
                        feature.coordinates[0][index],
                        feature.coordinates[0][index + 1]
                            ? feature.coordinates[0][index + 1]
                            : feature.coordinates[0][0],
                    ),
                );
            });
        } else if (feature.type === Constants.geojsonTypes.LINE_STRING) {
            feature.coordinates.forEach((element, index) => {
                if (index < feature.coordinates.length - 1) {
                    let features = turf.points([
                        feature.coordinates[index],
                        feature.coordinates[index + 1]
                            ? feature.coordinates[index + 1]
                            : feature.coordinates[0],
                    ]);

                    let center = turf.center(features);
                    push(
                        createVertex(
                            feature.id,
                            center.geometry?.coordinates,
                            '0.0',
                            false,
                            feature.coordinates[index],
                            feature.coordinates[index + 1] === undefined
                                ? feature.coordinates[0]
                                : feature.coordinates[index + 1],
                        ),
                    );
                }
            });
        }
    };

    // create's vertex in modify mode
    const createVertex = (
        parentId,
        coordinates,
        path,
        selected,
        start,
        end,
    ) => {
        const isMeters = store.state.isMeters;

        if (start !== undefined && end !== undefined) {
            let from = turf.point(start);
            let to = turf.point(end);
            let distance: any = (
                turf.distance(from, to, {
                    units: 'miles',
                }) / 0.00062137
            ).toFixed(2);
            return {
                type: 'Feature',
                properties: {
                    meta: 'vertex',
                    parent: parentId,
                    coord_path: path,
                    active: selected ? 'true' : 'false',
                    totalMeasurementLabel: `${Math.round(
                        (Math.round(distance) *
                            100 *
                            (isMeters ? 1 : 1.09361)) /
                            100,
                    )} ${isMeters ? 'm' : 'yd'}`,
                },
                geometry: {
                    type: 'Point',
                    coordinates,
                },
            };
        }
    };

    // toggle vertex label visibility based on geo record visibility.
    const toggleVertexLabelVisibility = (
        map: mapboxgl.Map,
        geoRecord: any,
        isVisible: boolean,
    ) => {
        const source = map.getSource('geo-records-vertex-label');
        const features = source._data.features;
        features.forEach((element: any) => {
            if (element.id === geoRecord.id) {
                element.properties.isVisible = isVisible;
            }
        });
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    // generate measure vertex label
    const generateMeasureLabel = (
        start: any,
        end: any,
        geoRecord: any,
        isMeters: boolean,
    ) => {
        if (start !== undefined && end !== undefined) {
            let from = turf.point(start);
            let to = turf.point(end);
            let distance: any = (
                turf.distance(from, to, {
                    units: 'miles',
                }) / 0.00062137
            ).toFixed(2);

            return {
                id: geoRecord.id,
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [start, end],
                },
                properties: {
                    title: `${Math.round(
                        (Math.round(distance) *
                            100 *
                            (isMeters ? 1 : 1.09361)) /
                            100,
                    )} ${isMeters ? 'm' : 'yd'}`,
                    isVisible: true,
                    vertexLabelState: geoRecord.vertexLabelState
                        ? geoRecord.vertexLabelState
                        : geoRecord.properties.vertexLabelState,
                },
            };
        }
    };

    // update measure vertex label
    const updateVertexLabel = (
        geoRecord: any,
        map: mapboxgl,
        isMeters: boolean,
    ) => {
        let vertexLabels: any = [];
        let start, end, vertexLabelState, smoothCount;

        removeVertexLabel(map, geoRecord);

        if (geoRecord.type === 'Feature') {
            vertexLabelState = geoRecord.properties.vertexLabelState;
            smoothCount = geoRecord.properties.smoothCount;
        } else {
            vertexLabelState = geoRecord.vertexLabelState;
            smoothCount = geoRecord.smoothCount;
        }

        const source = map.getSource('geo-records-vertex-label');
        const features = source._data.features;

        if (geoRecord.geometry.type === 'Polygon') {
            if (geoRecord.geometry.coordinates[0]) {
                geoRecord.geometry.coordinates[0].forEach((element, index) => {
                    start = geoRecord.geometry.coordinates[0][index];
                    end = geoRecord.geometry.coordinates[0][index + 1];
                    if (vertexLabelState === 'on' && smoothCount === 0) {
                        vertexLabels.push(
                            generateMeasureLabel(
                                start,
                                end,
                                geoRecord,
                                isMeters,
                            ),
                        );
                    }
                });
            }
        } else if (geoRecord.geometry.type === 'LineString') {
            if (geoRecord.geometry.coordinates) {
                geoRecord.geometry.coordinates.forEach((element, index) => {
                    start = geoRecord.geometry.coordinates[index];
                    end = geoRecord.geometry.coordinates[index + 1];
                    if (vertexLabelState === 'on' && smoothCount === 0) {
                        vertexLabels.push(
                            generateMeasureLabel(
                                start,
                                end,
                                geoRecord,
                                isMeters,
                            ),
                        );
                    }
                });
            }
        }
        vertexLabels = vertexLabels.filter(Boolean);
        vertexLabels.forEach((x) => {
            features.push(x);
        });

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    // removes vertex label from map
    const removeVertexLabel = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records-vertex-label');
        let features = source._data.features;
        features = features.filter((x) => x.id !== geoRecord.id);
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    return {
        createVertex,
        toggleVertexLabelVisibility,
        generateMeasureLabel,
        removeVertexLabel,
        updateVertexLabel,
        generateDrawLayerLabel,
    };
};
